import { PricingConfig } from 'hooks/billing/usePricingConfig';
import { Alert, ALERT_TYPES, AlertType, QuotaResponse } from 'types/billing';

/**
 * Derives the current billing alert from a quota snapshot and pricing config.
 * Primary derivation path (option C — per Shlomo: "API returns %, FE decides").
 * Pure function — no side effects, no async.
 *
 * Severity rank (high → low): blocked > overage_active > percentage_threshold > trial_ending > trial_active
 * Banner eligibility: approaching/overage/blocked only (D13 — trial = page hero, not banner).
 *
 * @param quota QuotaResponse from /quota
 * @param pricingConfig PricingConfig (for alert thresholds)
 * @returns Alert, or undefined when no active alert
 */
export const deriveAlert = (
  quota: QuotaResponse,
  pricingConfig: PricingConfig,
): Alert | undefined => {
  if (quota.blocked) {
    return { type: ALERT_TYPES.blocked, value: quota.blocked.reason };
  }

  // Trial is checked before percentage thresholds so trial users never get
  // a banner-eligible alert — they see the BillingHero only (D13).
  // Guard covers both active (active=true) and lapsed (active=false) trials:
  // a lapsed trial user sees TrialBannerEnded, not an approaching-limit hero.
  if (quota.trial) {
    if (!quota.trial.active) {
      return undefined;
    }

    const type: AlertType =
      quota.trial.days_remaining <= pricingConfig.trialEndingThresholdDays
        ? ALERT_TYPES.trialEnding
        : ALERT_TYPES.trialActive;

    return { type, value: quota.trial.days_remaining };
  }

  if (quota.overage?.active) {
    return { type: ALERT_TYPES.overageActive, value: quota.overage.credits };
  }

  // No plan means no credit pool to be approaching the limit of — suppress
  // the percentage_threshold alert for plan-less accounts (e.g. trial-lapsed
  // users whose quota.trial is null but quota.plan is also null).
  if (!quota.plan) {
    return undefined;
  }

  const thresholds = [...pricingConfig.alertThresholds.approaching].sort(
    (a, b) => b - a,
  );
  const crossedThreshold = thresholds.find((t) => quota.usage_pct >= t);

  if (crossedThreshold !== undefined) {
    return { type: ALERT_TYPES.percentageThreshold, value: crossedThreshold };
  }

  return undefined;
};

/**
 * Returns true if the given alert type should render as the global banner.
 * Trial alerts are page-hero-only (D13).
 */
export const isBannerEligible = (alert: Alert): boolean =>
  alert.type === ALERT_TYPES.percentageThreshold ||
  alert.type === ALERT_TYPES.overageActive ||
  alert.type === ALERT_TYPES.blocked;
